import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import Icon from './Icon.jsx'
import Popover from './Popover.jsx'
import GlyphTile from './GlyphTile.jsx'
import { lucideGlyph } from './LucideIcon.jsx'
import { TILE } from '../lib/tileLook.js'
import { listIdFromParam, listPath } from '../lib/lists.js'
import { readJSON, writeJSON } from '../lib/localStore.js'
import { useHoverMenu } from '../lib/useHoverMenu.js'

// Which board the picker is pointed at, remembered the way the board's own date and view
// are. One memory for the whole app rather than one per visit: the board you work in is a
// standing fact about you, not about the page you happen to have open.
const OPEN_KEY = 'fgs-nav-board'
// What that memory holds when the schedule is the board in question. Lists are held by id,
// and an id is a uuid, so the two can never collide.
const BOARD = 'board'

/**
 * Which board you are working in, as a picker in the nav.
 *
 * Not a link with a menu hanging off it: the control's VALUE is a board, shown with that
 * board's own icon and colour, and opening it offers the others. The schedule and every
 * list are peers in that list, which is the shape the product actually has — a list is the
 * board turned round, days in the cells and people written in them — rather than one being
 * a section you go into and find tiles in.
 *
 * That the whole control opens, rather than the word navigating and a chevron opening, is
 * what makes it a picker instead of the ambiguous kind of button that might do either
 * depending on where the pointer landed. It costs the board a click it used to have for
 * free, which hover gives straight back: the menu is open by the time the pointer arrives.
 *
 * Click works too, and has to — hover reaches neither a phone nor a keyboard, and this is
 * the way to every board in the app. The grace period on leaving is SplitButton's, for the
 * same reason: a pointer crossing the gap between the button and the panel briefly leaves
 * both, and closing on that flicker feels broken.
 *
 * With no lists there is nothing to pick between, so this is a plain link and the nav looks
 * exactly as it did before lists existed.
 */
export default function NavBoard({ listsOn }) {
  const anchorRef = useRef(null)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const menu = useHoverMenu()

  // Live, so a list renamed, retired or added in Settings reaches this picker without a
  // reload — ['lists'] is one of useRealtime's keys. Not asked for at all by a company that
  // does not use lists, which is most of them.
  const { data } = useQuery({
    queryKey: ['lists'],
    queryFn: () => api.get('/api/lists'),
    enabled: Boolean(listsOn),
  })
  const lists = data?.lists || []

  // Which board the address names, if it names one at all. The id comes off the address
  // rather than from a match against every list, so a link whose name half is stale still
  // resolves — the same rule the list page itself follows.
  const openListId = pathname.startsWith('/lists/') ? listIdFromParam(pathname.split('/')[2]) : null
  const onBoard = pathname === '/board'
  const openKey = onBoard ? BOARD : openListId
  // Lit for any board, including the index — everything this control leads to.
  const here = onBoard || pathname === '/lists' || pathname.startsWith('/lists/')

  /**
   * The board the picker is pointed at: the last one you were on, which is not the same as
   * the page you are on now.
   *
   * Somebody who keeps a list open all day and steps into the Directory for a phone number
   * should find the nav still reading that list, not quietly reset to the schedule — the
   * picker is where they work, and leaving it for a minute is not changing it. Kept in
   * storage so it survives a reload and tomorrow morning too.
   */
  const [openBoard, setOpenBoard] = useState(() => readJSON(OPEN_KEY, BOARD))
  useEffect(() => {
    // Only a board teaches this. Every other page leaves the memory exactly as it was,
    // which is the whole point of having one.
    if (!openKey || openKey === openBoard) return
    setOpenBoard(openKey)
    writeJSON(OPEN_KEY, openKey)
  }, [openKey, openBoard])

  /**
   * That memory resolved against the lists that still exist. A remembered list that has
   * since been retired or deleted is simply not found, and the picker falls back to the
   * schedule rather than offering a door into nothing.
   */
  const shown = useMemo(
    () => (openBoard === BOARD ? null : lists.find((l) => l.id === openBoard) || null),
    [lists, openBoard],
  )
  const shownPath = shown ? listPath(shown) : '/board'
  // Whether the value names the page you are actually on — true only on that board itself,
  // never on the Directory or Settings, where the value is a memory rather than a statement
  // about this page.
  const showsWhereYouAre = openKey === (shown ? shown.id : BOARD)

  const go = (path) => {
    menu.close()
    navigate(path)
  }

  /**
   * Click goes to the board the value names, unless you are already standing on it — and
   * then it opens the menu instead.
   *
   * Reading a board's name and having to open a menu to pick the one thing it already says
   * was a click spent on nothing. Standing ON that board there is nothing to navigate to,
   * so the click is free to do the only useful thing left.
   *
   * Hover still opens the menu from either state, so the quick path to another board is
   * unchanged. Every state stays reachable by touch, which has no hover: a tap lands you on
   * the board, and a second tap there opens the menu.
   */
  const onTriggerClick = () => (showsWhereYouAre ? menu.toggle() : go(shownPath))

  // Nothing to pick between. A picker offering one option is a click to choose the only
  // thing there is.
  if (!lists.length) return <NavLink to="/board">Board</NavLink>

  return (
    <div
      className={`nav-picker ${here ? 'on' : ''}`}
      ref={anchorRef}
      onMouseEnter={menu.openNow}
      onMouseLeave={menu.closeSoon}
    >
      <button
        type="button"
        className="nav-picker-trigger"
        aria-haspopup="menu"
        aria-expanded={menu.open}
        // Says what the click will do, which is not the same in both states, and names the
        // board either way — so a screen reader hears the job of the control before its
        // contents, and never hears a bare "Board" that could mean two different clicks.
        aria-label={
          showsWhereYouAre
            ? `${shown ? shown.name : 'Board'}. Choose another board`
            : `Go to ${shown ? shown.name : 'the board'}`
        }
        onClick={onTriggerClick}
      >
        {shown ? (
          <GlyphTile icon={lucideGlyph(shown.icon)} tint={shown.color} size={19} />
        ) : (
          <GlyphTile {...TILE.board} size={19} />
        )}
        <span className="nav-picker-value">{shown ? shown.name : 'Board'}</span>
        <Icon name="down" size={12} strokeWidth={2.4} />
      </button>

      <Popover
        open={menu.open}
        anchorRef={anchorRef}
        close={menu.close}
        className="filter-panel nav-picker-menu"
        role="menu"
        label="Boards"
        align="left"
        onMouseEnter={menu.openNow}
        onMouseLeave={menu.closeSoon}
      >
        {/* The schedule stands apart, above the rule and carrying its own weight. It is
            what this company runs on — every list is something kept BESIDE it — and a menu
            that listed it as the first of four equal rows would say the opposite. Its tile
            does half the work already: the brand's colour, where every row below wears one
            a list picked for itself. */}
        <button
          type="button"
          role="menuitem"
          aria-current={onBoard ? 'page' : undefined}
          className={`ctx-item nav-picker-board ${onBoard ? 'active' : ''}`}
          onClick={() => go('/board')}
        >
          <GlyphTile {...TILE.board} size={22} />
          <span>Board</span>
        </button>

        {/* The rule alone separates them. A caption over the lists would be naming the
            obvious — every row below wears a list's own colour, and the one above is the
            company's — and it put a line of shouting type in a menu of four things. */}
        <div className="ctx-sep" />

        {lists.map((l) => (
          <button
            key={l.id}
            type="button"
            role="menuitem"
            aria-current={l.id === openListId ? 'page' : undefined}
            className={`ctx-item ${l.id === openListId ? 'active' : ''}`}
            onClick={() => go(listPath(l))}
          >
            {/* The same tile the list wears on its own header and on the index, so a row is
                recognised by colour before the name is read. */}
            <GlyphTile icon={lucideGlyph(l.icon)} tint={l.color} size={20} />
            <span>{l.name}</span>
          </button>
        ))}

        {/* The page ABOUT the lists rather than one of them — descriptions, and where an
            owner sets one up. Only worth offering once there are enough lists for a page of
            them to say something this menu does not: with one or two, every list is already
            named here and the index is the same short list again with more words round it.
            Quiet, and inside the group it belongs to, so it never reads as another list. */}
        {lists.length >= 3 && (
          <button
            type="button"
            role="menuitem"
            className="ctx-item nav-picker-all"
            onClick={() => go('/lists')}
          >
            <span className="ctx-item-icon">
              <Icon name="sort" size={14} />
            </span>
            <span>All lists</span>
          </button>
        )}
      </Popover>
    </div>
  )
}
